import { CustomerMultiSelect } from "@/components/data-driven-inputs/selects/customer-select/CustomerMultiSelect";
import { Filter } from "@/components/search-filter/Filter";
import { FilterSection } from "@/components/search-filter/FilterSection";
import { RangeControl } from "@/features/search-filter/components/RangeControl";
import { VehicleTypeCollapsible } from "@/features/search-filter/components/sections/VehicleTypeFilterSection";
import { useFetchAllRouteAgreementSailingCategories } from "@/features/search-filter/hooks/FilterFetchers/UseFetchAllRouteAgreementSailingCategories";
import { useFetchAllVehicleTypes } from "@/features/search-filter/hooks/FilterFetchers/UseFetchAllVehicleTypes";
import { sailingTypeOptionsWithCodeM } from "@/features/search-filter/utils/sortings";
import { useCurrencySelection } from "@/redux/profile/hooks/UseCurrencySelection";
import { CurrencySelect } from "@/components/data-driven-inputs/selects/currency-select/CurrencySelect";
import { DateTextInput } from "@stenajs-webui/calendar";
import { Column, Row, Text } from "@stenajs-webui/core";
import { ZIndex } from "@/lib/utils/ZIndexEnum";
import {
  Banner,
  Spinner,
  stenaInfoCircle,
  stenaPerformance,
  stenaSailingRoundTrip,
  stenaSailingRoute,
  stenaSailingVehicle,
  stenaUserMultiple,
} from "@stenajs-webui/elements";
import { FilterCheckbox } from "@stenajs-webui/filter";
import { groupBy } from "lodash";
import { Fragment, useContext, useMemo } from "react";
import { validatePriceListFilter } from "../../lib/priceListFilterInput";
import { SailingCategory } from "../../types/PriceListRow";
import { PriceListFilterChips } from "./PriceListFilterChips";
import { RoutesFilterSection } from "./RoutesFilterSection";
import {
  LengthRange,
  PriceListFilterContext,
  PriceListFilterContextType,
  getChipsFromFilterState,
  sectionIdConstants,
} from "./PriceListFilterContext";

const CustomerSection = () => {
  const { filterState, dispatch, toggleSectionId, openSectionIds } = useContext(
    PriceListFilterContext
  ) as PriceListFilterContextType;

  const sectionId = sectionIdConstants.customer;
  return (
    <FilterSection
      leftIcon={stenaUserMultiple}
      label={"Customer"}
      isOpen={!!openSectionIds.includes(sectionId)}
      setIsOpen={() => toggleSectionId(sectionId)}
    >
      <CustomerMultiSelect
        value={filterState.selectedCustomers}
        onValueChange={(value) =>
          dispatch({ type: "SET_CUSTOMERS", payload: value })
        }
        isClearable
      />
    </FilterSection>
  );
};

const SailingCategorySection = () => {
  const { filterState, dispatch, toggleSectionId, openSectionIds } = useContext(
    PriceListFilterContext
  ) as PriceListFilterContextType;
  const { routeAgreementSailingCategories, loading } =
    useFetchAllRouteAgreementSailingCategories();
  const sectionId = sectionIdConstants.sailingCategory;

  const sortedCategories = useMemo(
    () => sailingTypeOptionsWithCodeM(routeAgreementSailingCategories),
    [routeAgreementSailingCategories]
  );

  return (
    <FilterSection
      leftIcon={stenaPerformance}
      label={"Sailing category"}
      isOpen={!!openSectionIds.includes(sectionId)}
      setIsOpen={() => toggleSectionId(sectionId)}
    >
      {loading ? (
        <Spinner />
      ) : (
        sortedCategories.map((item) => {
          const code = item.code as SailingCategory;
          return (
            <FilterCheckbox
              key={item.id}
              label={item.name}
              value={filterState.selectedSailingCategories.includes(code)}
              onValueChange={() =>
                dispatch({ type: "TOGGLE_SAILING_CATEGORY", payload: code })
              }
            />
          );
        })
      )}
    </FilterSection>
  );
};

const VehicleTypeSection = () => {
  const { filterState, dispatch, toggleSectionId, openSectionIds } = useContext(
    PriceListFilterContext
  ) as PriceListFilterContextType;
  const { items: allVehicleTypes } = useFetchAllVehicleTypes();
  const sectionId = sectionIdConstants.vehicleTypes;

  const grouped = useMemo(
    () => groupBy(allVehicleTypes, (item) => item.accompanied),
    [allVehicleTypes]
  );

  const setVehicleTypes = (next: typeof filterState.selectedVehicleTypes) =>
    dispatch({ type: "SET_VEHICLE_TYPES", payload: next });

  const checkboxClickHandler = (_: boolean, id: string) => {
    const entityToToggle = allVehicleTypes.find((item) => item.id === id);
    if (!entityToToggle) return;
    const exists = filterState.selectedVehicleTypes.some(
      (vt) => vt.id === entityToToggle.id
    );
    setVehicleTypes(
      exists
        ? filterState.selectedVehicleTypes.filter(
            (vt) => vt.id !== entityToToggle.id
          )
        : [...filterState.selectedVehicleTypes, entityToToggle]
    );
  };

  return (
    <FilterSection
      leftIcon={stenaSailingVehicle}
      label={"Vehicle type"}
      isOpen={!!openSectionIds.includes(sectionId)}
      setIsOpen={() => toggleSectionId(sectionId)}
    >
      {Object.keys(grouped).map((accompanied) => (
        <Fragment key={accompanied}>
          <VehicleTypeCollapsible
            selectedVehicleTypes={filterState.selectedVehicleTypes}
            setVehicleTypes={setVehicleTypes}
            label={accompanied === "true" ? "Accompanied" : "Unaccompanied"}
            checkboxClickHandler={checkboxClickHandler}
            groupedVehicleType={grouped[accompanied]}
          />
        </Fragment>
      ))}
    </FilterSection>
  );
};

const LengthRangeSection = () => {
  const { filterState, dispatch, toggleSectionId, openSectionIds } = useContext(
    PriceListFilterContext
  ) as PriceListFilterContextType;
  const sectionId = sectionIdConstants.lengthRange;

  return (
    <FilterSection
      leftIcon={stenaSailingRoundTrip}
      label={"Length range *"}
      isOpen={!!openSectionIds.includes(sectionId)}
      setIsOpen={() => toggleSectionId(sectionId)}
    >
      <RangeControl<LengthRange>
        labelFrom={"From (m)"}
        labelTo={"To (m)"}
        value={filterState.lengthRange}
        valueFrom={filterState.lengthRange.from}
        valueTo={filterState.lengthRange.to}
        onValueChange={(lengthRange) =>
          dispatch({ type: "SET_LENGTH_RANGE", payload: lengthRange })
        }
        onFromBlur={() =>
          dispatch({
            type: "SET_LENGTH_RANGE",
            payload: {
              ...filterState.lengthRange,
              to: filterState.lengthRange.from,
            },
          })
        }
        maxNumbers={3}
        maxDecimals={0}
        selectOnFocus
      />
    </FilterSection>
  );
};

const DateAndCurrencyRow = () => {
  const { filterState, dispatch } = useContext(
    PriceListFilterContext
  ) as PriceListFilterContextType;
  return (
    <Row
      gap={2}
      alignItems={"flex-end"}
      justifyContent={"space-between"}
      indent={2}
      spacing={1}
    >
      <Column>
        <Text size={"small"}>{"Date *"}</Text>
        <DateTextInput
          value={filterState.date}
          onValueChange={(date) =>
            dispatch({ type: "SET_DATE", payload: date })
          }
          zIndex={ZIndex.highest}
        />
      </Column>
      {/*
        The backend does not convert the price list yet, so choosing a
        display currency would not change anything that comes back. Hidden
        until it does, along with the chip and the required-field check.
        The filter input still carries a currency because the schema asks
        for one - see PRICE_LIST_CURRENCY in priceListFilterInput.

        <Column>
          <Text size={"small"}>{"Display currency *"}</Text>
          <CurrencySelect
            value={currency}
            onValueChange={handleCurrencyChange}
            containerWidth={"110px"}
          />
        </Column>
      */}
    </Row>
  );
};

export const PriceListFilter = ({
  onSearch,
  onClear,
}: {
  onSearch: () => void;
  onClear: () => void;
}) => {
  const {
    clearFilter,
    filterState,
    setSelectedFilterChips,
    filterIsOpen,
    setFilterIsOpen,
  } = useContext(PriceListFilterContext) as PriceListFilterContextType;
  const { currency } = useCurrencySelection();

  const validation = validatePriceListFilter(filterState, currency);

  const searchHandler = () => {
    setSelectedFilterChips(getChipsFromFilterState(filterState, currency));
    onSearch();
    setFilterIsOpen(false);
  };

  const clearHandler = () => {
    clearFilter();
    onClear();
  };

  return (
    <Filter
      filterIsOpen={filterIsOpen}
      setFilterIsOpen={setFilterIsOpen}
      clearFilter={clearHandler}
      onSearch={searchHandler}
      searchDisabled={!validation.valid}
      filterChips={<PriceListFilterChips />}
    >
      <DateAndCurrencyRow />
      <RoutesFilterSection />
      <LengthRangeSection />
      <VehicleTypeSection />
      <SailingCategorySection />
      <CustomerSection />
      {!validation.valid && (
        <Banner
          icon={stenaInfoCircle}
          variant={"info"}
          text={`Required to search: ${validation.missing.join(", ")}.`}
        />
      )}
    </Filter>
  );
};
